import { toDictionary } from "@tokenterminal/core/utils/toDictionary"
import { useMetricBreakdown } from "modules/projects/hooks/useMetricBreakdowns"
import { useMetricsConfigurationList } from "modules/projects/hooks/useMetricsConfigurationList"
import { useMemo } from "react"
import { toSlug } from "../../utils"
import type { Interval } from "@tokenterminal/tt-analytics-api-types/dist/api/zoom"

/**
 * Returns an object of metric breakdown data organized by market sector slug.
 * @example
 * const { data: metricBreakdownBySectorSlug } = useMetricBreakdownBySectorSlug(['fees', 'market_cap_fully_diluted'])
 *
 * // metricBreakdownBySectorSlug = {
 * //   "gaming": {
 * //     "fees": { value: 200, change: 20 },
 * //     "market_cap_fully_diluted": { value: 2000, change: 200 },
 * //   },
 * //   "lending": {
 * //     "fees": { value: 100, change: 10 },
 * //     "market_cap_fully_diluted": { value: 1000, change: 100 },
 * //   },
 * //   ...
 * // }
 */

export const useMetricBreakdownBySectorSlug = (
  metricIds: string[],
  interval: Interval = "7d"
) => {
  const { data: metricConfigurations = [] } = useMetricsConfigurationList()
  const metricConfigurationsById = useMemo(
    () => toDictionary(metricConfigurations, "metric_id"),
    [metricConfigurations]
  )
  const { data: breakdownResult } = useMetricBreakdown({
    groupBy: "market-sector",
    metricIds,
    interval,
  })

  return useMemo(() => {
    return (breakdownResult?.data || [])?.reduce(
      (acc, item) => {
        const slug = toSlug(item.market_sector_id || "")
        metricIds.forEach((metricId) => {
          if (!acc[slug]) {
            acc[slug] = {}
          }

          const data = item.metrics[metricId]
          const value = metricConfigurationsById.get(metricId)?.static
            ? data?.latest || null
            : data?.sum || null

          acc[slug][metricId] = {
            value,
            change: data?.change,
          }
        })
        return acc
      },
      {} as Record<
        string,
        Record<string, { value: number | null; change: number | null }>
      >
    )
  }, [breakdownResult, metricIds, metricConfigurationsById])
}
